/**
 * Filling a form: the picker, the fill screen, the widgets, and the panel on the daily report.
 *
 * ⚠ THIS IS THE CHROME ONLY, AND THE CHROME IS THE SMALLER HALF. What a foreman actually READS on
 * this screen is the FORM ITSELF — "Are there fluid leaks on the ground?" and thirty-five more like
 * it — and every one of those strings is tenant-owned data in a JSONB column, not code. The
 * translations exist (`form_version_translations`, written by the panel at
 * /forms/builder/:id/translations) and the resolvers exist (modules/forms/i18n.js `tLabel`, `tRow`,
 * `tOption`), but ONLY the unauthenticated QR page calls them: PublicInspection.jsx hydrates its
 * nodes, and FormRenderer / CanvasRenderer / FieldInput do not. So a tenant who translates their JSA
 * gets Spanish at a sticker and English inside the application, on the same document.
 *
 * That is the single highest-value thing left in this feature and it is NOT a translation job — it is
 * threading `translations` + `lang` through four render components, including the option path where
 * the durable VALUE and the shown LABEL must stay separate (see `tOption`). Half of it would be worse
 * than none: labels translated with options left raw is exactly how a sheet filled in Spanish stops
 * storing the same answer as the same sheet filled in English.
 */
export default {
  // ── module chrome ─────────────────────────────────────────────────────────────────────────────
  forms: 'Forms',
  backToForms: 'Back to forms',

  // ── the module's own sub-nav ──────────────────────────────────────────────────────────────────
  navTemplates: 'Templates',
  navAssignments: 'Assignments',
  navSigningRules: 'Signing rules',
  navFill: 'Fill a form',
  navSubmissions: 'Submissions',

  // ── permission ────────────────────────────────────────────────────────────────────────────────
  cannotFillTitle: 'You can’t fill out forms',
  cannotFillBody: 'Your account doesn’t have the “submit forms” permission.',
  cannotFillBodyAskAdmin: 'Your account doesn’t have the “submit forms” permission. Ask an '
    + 'administrator to grant it.',

  // ── the picker ────────────────────────────────────────────────────────────────────────────────
  fillOutAForm: 'Fill out a form',
  pickAForm: 'Pick a form to start, or continue one you’ve already begun.',
  loadingForms: 'Loading forms…',
  searchForms: 'Search forms…',
  continueWhereYouLeftOff: 'Continue where you left off',
  noPublishedForms: 'No published forms are available yet.',
  noFormsMatch: 'No forms match your search.',

  // ── the fill screen ───────────────────────────────────────────────────────────────────────────
  openingForm: 'Opening form…',
  couldNotOpen: 'Couldn’t open this form',
  practiceRunTitle: 'That was a practice run',
  alreadySubmitted: 'This form has already been submitted, so it’s read-only.',
  autosaveHint: 'Your answers save automatically as you type.',
  saving: 'Saving…',
  saved: 'Saved',
  notSaved: 'Not saved',
  submitting: 'Submitting…',
  submit: 'Submit',
  submitted: 'Submitted',
  fillOutAnother: 'Fill out another form',
  done: 'Done',

  // ── the widgets ───────────────────────────────────────────────────────────────────────────────
  notSigned: 'Not signed',
  signature: 'Signature',
  remove: 'Remove',
  removeRow: 'Remove row',
  noRows: 'No rows',
  noRowsYet: 'No rows yet — add the first one.',
  addRow: '+ Add row',
  crewListUnavailable: 'Crew list unavailable.',
  loadingCrews: 'Loading crews…',
  noCrewsYet: 'No crews have been set up yet.',
  noCrewsYetHelp: 'Add one under Timekeeping → Crews.',
  noFieldsYet: 'This form has no fields yet.',
  noContentYet: 'This form has no content yet.',

  // ── the panel on a record (the daily report draws this) ───────────────────────────────────────
  formsOnThisRecord: 'Forms on this record',
  addAForm: 'Add a form',
  couldNotLoadRecordForms: 'Couldn’t load this record’s forms.',
  couldNotLoadRecordFormsHelp1: 'This does',
  couldNotLoadRecordFormsNot: 'not',
  couldNotLoadRecordFormsHelp2: 'mean none were filled in — the list is unknown right now. Nothing '
    + 'else on this page is affected.',
  tryAgain: 'Try again',
  carriedForward: 'carried forward',
  onlyYourOwn: 'You can only see forms you filled in yourself.',
  chooseAForm: 'Choose a form',
  couldNotLoadFormList: 'Couldn’t load the list of forms. Try again in a moment.',
  noPublishedFormsYet: 'No published forms yet. A form has to be published before it can be filled in.',
  alreadyFiled: 'already filed — open it above',
  required: 'Required',
  otherForms: 'Other forms',
  close: 'Close',
}
